import * as React from "react";

import { cn } from "@app//lib/utils";

/**
 * A clickable value inside a table cell that still COPIES. A `<button>` is the
 * natural element, but browsers leave button contents out of a text selection —
 * select a grid of numbers, paste it, and every linked figure is missing. A span
 * with the button role keeps the number in the selection and keeps the keyboard
 * (Enter / Space) and the accessible name a button would have had.
 */
export const CellLink = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement> & { onClick: () => void; disabled?: boolean }
>(({ className, onClick, disabled = false, onKeyDown, children, ...props }, ref) => (
  <span
    ref={ref}
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-disabled={disabled || undefined}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(event) => {
      onKeyDown?.(event);
      if (disabled || event.defaultPrevented) return;
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onClick();
      }
    }}
    className={cn(
      "cursor-pointer select-text hover:underline hover:text-gray-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-300 rounded-sm",
      disabled && "cursor-default opacity-50 hover:no-underline",
      className,
    )}
    {...props}
  >
    {children}
  </span>
));
CellLink.displayName = "CellLink";
